import { For, Show, onCleanup, onMount, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { AnamneseSectionScope, InfoFieldScope, PatientDetailScope } from 'mormaii-web-presentation/patient-detail'

import { historyCell } from '../../../components/history-cells'
import { PatientAnalytics } from '../../../components/patient-analytics'
import { StatusBands } from '../../../components/status-bands'
import { DataTable, Pager } from '../../../components/table'
import { BucketDialog } from '../../../components/bucket-readings'
import { ErrorNotice, Loading, Panel, Spinner } from '../../../components/ui'
import { Button } from '../../../components/filters'
import { Icon } from '../../../components/icons'

import Css from './patient-detail.module.scss'

/**
 * A ficha de uma pessoa.
 *
 * Montada em DOIS lugares - vindo de Colaboradores e vindo dos Monitoramentos
 * Compartilhados - e nao sabe de qual. Ate o rotulo do botao de voltar vem do scope,
 * porque ele nomeia a lista de onde se veio.
 *
 * ## O historico e UMA tabela em dois estados
 *
 * Sem dia aberto ela lista os DIAS; com dia aberto lista as LEITURAS daquele dia. A
 * tela troca inteira em vez de expandir uma linha - as colunas sao as mesmas e o que
 * muda e o que cada linha representa.
 */
export function PatientDetailView(props: ViewProps<PatientDetailScope>): JSX.Element {
    return (
        <Show when={!props.scope.loading || props.scope.name !== '-'} fallback={<Loading />}>
            <Show when={!props.scope.error} fallback={<ErrorNotice message={props.scope.error!} />}>
                <div class={Css.screen}>
                    <button type="button" onClick={() => void props.scope.onBack()} class={Css.back}>
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="m15 18-6-6 6-6" />
                        </svg>
                        {props.scope.backLabel}
                    </button>

                    <Identification scope={props.scope} />

                    <Show when={props.scope.analytics}>{(analytics) => <PatientAnalytics scope={analytics()} />}</Show>

                    <Show when={props.scope.anamneseSections.length > 0}>
                        <Panel title="Anamnese">
                            <div class={Css.anamnesis}>
                                <For each={props.scope.anamneseSections}>
                                    {(section) => <AnamneseBlock scope={section} />}
                                </For>
                            </div>
                        </Panel>
                    </Show>

                    <Show when={props.scope.targets.length > 0}>
                        <Panel title="Metas">
                            <div class={Css.grid}>
                                <For each={props.scope.targets}>{(field) => <Field scope={field} />}</For>
                            </div>
                        </Panel>
                    </Show>

                    <History scope={props.scope} />

                    <Show when={props.scope.bands}>{(bands) => <StatusBands scope={bands()} />}</Show>
                </div>
            </Show>
        </Show>
    )
}

/** Quem e a pessoa: foto, nome, os dados de contato e se esta ativa. */
function Identification(props: { scope: PatientDetailScope }): JSX.Element {
    const initials = () =>
        props.scope.name
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() ?? '')
            .join('')

    return (
        <Panel>
            <div class={Css.identity}>
                <Show when={props.scope.photo} fallback={<span class={Css.initials}>{initials()}</span>}>
                    {(photo) => <img src={photo()} alt="" class={Css.photo} />}
                </Show>

                <div class={Css.texts}>
                    <div class={Css.nameRow}>
                        <h2 class={Css.name}>{props.scope.name}</h2>
                        <span class={Css.status} classList={{ [Css.active]: props.scope.active }}>
                            {props.scope.active ? 'Ativo' : 'Inativo'}
                        </span>
                    </div>

                    <div class={Css.grid}>
                        <For each={props.scope.identification}>{(field) => <Field scope={field} />}</For>
                    </div>
                </div>
            </div>
        </Panel>
    )
}

function AnamneseBlock(props: { scope: AnamneseSectionScope }): JSX.Element {
    return (
        <div class={Css.block}>
            <h3 class={Css.blockTitle}>{props.scope.title}</h3>
            {/*
                Blocos de um campo so nao ganham grade - e um paragrafo embaixo do
                titulo, que e como a informacao se le.
            */}
            <div
                class={Css.blockFields}
                classList={{
                    [Css.manyUp]: props.scope.fields.length > 2,
                    [Css.twoUp]: props.scope.fields.length === 2
                }}
            >
                <For each={props.scope.fields}>{(field) => <Field scope={field} />}</For>
            </div>
        </div>
    )
}

function Field(props: { scope: InfoFieldScope }): JSX.Element {
    return (
        <div class={Css.field}>
            <span class={Css.fieldLabel}>{props.scope.label}</span>
            <span class={Css.fieldValue}>
                <span class={Css.fieldText}>{props.scope.value}</span>
                <Show when={props.scope.suffix}>
                    {(suffix) => (
                        <span class={Css.fieldSuffix} style={{ color: props.scope.suffixColor }}>
                            {suffix()}
                        </span>
                    )}
                </Show>
            </span>
        </div>
    )
}

function History(props: { scope: PatientDetailScope }): JSX.Element {
    /*
     * `Esc` volta aos dias.
     *
     * O reforco de teclado do mesmo caminho que o cabecalho desenha - e o gesto que
     * quem usa painel ja tenta quando entrou um nivel e quer sair. Nao substitui o
     * caminho visivel: um atalho que ninguem ve nao resolve descoberta.
     *
     * ## Na CAPTURA, e olhando o DOM
     *
     * Um dialogo aberto responde ao `Esc` primeiro, e sair do dia por baixo dele
     * deixaria na tela um dialogo que fala de uma tabela que nao esta mais ali.
     *
     * Perguntar ao scope (`bucket.open`) nao resolve, e foi o primeiro erro: no
     * borbulhamento o Kobalte ja fechou o dialogo e ja avisou quem o abriu, entao a
     * bandeira chega FALSA ao chegar aqui - um `Esc` fechava os dois de uma vez. Na
     * captura este ouvinte corre antes de qualquer um, e ai a pergunta e feita quando a
     * resposta ainda vale.
     *
     * E a pergunta e ao DOM, `[role="dialog"]`, e nao a uma bandeira: esta tela tem mais
     * de um dialogo - o da celula e o de editar limites -, e o que importa nao e QUAL
     * esta aberto, e sim que exista um por cima.
     */
    onMount(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            if (!props.scope.openDay) return
            if (document.querySelector('[role="dialog"]')) return
            void props.scope.onCloseDay()
        }

        window.addEventListener('keydown', onKeyDown, true)
        onCleanup(() => window.removeEventListener('keydown', onKeyDown, true))
    })

    return (
        <Panel
            /*
             * Com um dia aberto, o cabecalho vira CAMINHO: o nivel de cima a esquerda,
             * clicavel, e o dia onde antes estava o titulo. Ver `parent` no `Panel` para
             * por que a saida deixou de ser uma acao no canto oposto.
             */
            title={props.scope.openDay ? props.scope.openDayLabel : 'Histórico por dia'}
            parent={
                props.scope.openDay
                    ? { label: 'Histórico por dia', onClick: () => void props.scope.onCloseDay() }
                    : undefined
            }
            actions={
                // So com um dia aberto: o relatorio e DO DIA, e na lista de dias nao
                // ha dia escolhido para imprimir (B-29).
                <Show when={props.scope.openDay}>
                    <Button
                        variant="outline"
                        disabled={
                            props.scope.loadingDay || props.scope.downloadingDay || props.scope.historyRows.length === 0
                        }
                        onClick={() => void props.scope.onDownloadDay()}
                    >
                        <Show when={props.scope.downloadingDay} fallback={<Icon name="file-download" size={16} />}>
                            <Spinner class={Css.reportSpinner} />
                        </Show>
                        {props.scope.downloadingDay
                            ? `Gerando… ${props.scope.dayReportProgress}`
                            : 'Relatório do dia (PDF)'}
                    </Button>
                </Show>
            }
            flush
        >
            <DataTable
                columns={props.scope.historyColumns.map((column) => ({
                    field: column.field,
                    label: column.label,
                    sortable: true,
                    sorted: column.sorted,
                    descending: column.descending,
                    onSort: column.onSort,
                    movable: column.movable,
                    onDrop: (source) => void column.onDrop(source)
                }))}
                rows={props.scope.historyRows}
                layout={props.scope.historyLayout}
                loading={props.scope.loadingDay}
                cell={historyCell}
                emptyText="Nenhuma medição no período"
            />

            <Show when={props.scope.bucket}>{(bucket) => <BucketDialog scope={bucket()} />}</Show>

            <Pager
                page={props.scope.historyPage}
                pageCount={props.scope.historyPageCount}
                pages={props.scope.historyPageWindow}
                onPrevious={() => void props.scope.onPreviousPage()}
                onNext={() => void props.scope.onNextPage()}
                onGoTo={(page) => void props.scope.onGoToPage(page)}
            />
        </Panel>
    )
}
